import { alpha, Box, darken, Stack, Typography, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import MultipleNumberInput from 'src/components/common/MultipleNumberInput/MultipleNumberInput';
import { QuantityInputItem } from '@repo/widget-utils/booking/domains/shared/types';

// --- Types ---

export type ExtraProductCardProps = {
    title: string;
    description?: string;
    price?: string;
    /** Optional image URL */
    imageUrl?: string;
    /** Image alt text (falls back to title if not provided) */
    imageAlt?: string;
    /** Quantity input items (uses shared QuantityInputItem type) */
    quantityInputs: QuantityInputItem[];
    /** Callback when quantity changes */
    onQuantityChange: (id: string, value: number) => void;
    /** Action button config */
    action: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
        loading?: boolean;
    };
    /** Optional error message */
    error?: string;
};

/**
 * Dumb (presentational) component for displaying an extra product card.
 *
 * Follows the visual pattern of ProductCard2Landscape but accepts all data via props.
 * Used for OCTO extras selection in checkout flow.
 *
 * @see docs/component-design-guide.md
 */
export function ExtraProductCard({
    title,
    description,
    price,
    imageUrl,
    imageAlt,
    quantityInputs,
    onQuantityChange,
    action,
    error,
}: ExtraProductCardProps) {
    const theme = useTheme();
    const customizations = useCustomizations();

    const hasImage = !!imageUrl;

    return (
        <Box sx={{ containerType: 'inline-size' }}>
            <Stack
                bgcolor={customizations.productCardColor}
                color={customizations.productCardTextColor}
                direction="column"
                p={{ xs: 2, sm: 3 }}
                gap={2}
                borderRadius={`${customizations.borderRadius * 3}px`}
                overflow="hidden"
                boxShadow={customizations.imageShadow ? theme.shadows[3] : undefined}
            >
                {/* Main two-column layout on large screens */}
                <Stack
                    direction="column"
                    gap={2}
                    sx={{
                        '@container (min-width: 600px)': {
                            flexDirection: 'row',
                            gap: 3,
                        },
                    }}
                >
                    {/* LEFT COLUMN: Image + Title/Description */}
                    <Stack
                        gap={2}
                        sx={{
                            '@container (min-width: 600px)': {
                                flex: 1,
                                minWidth: 0,
                            },
                        }}
                    >
                        {/* Image - inside the card with rounded corners */}
                        {hasImage && (
                            <Box
                                sx={{
                                    width: '100%',
                                    overflow: 'hidden',
                                    borderRadius: `${customizations.borderRadius * 2}px`,
                                    aspectRatio: '16 / 9',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt={imageAlt ?? title}
                                    width="100%"
                                    height="100%"
                                    sx={{
                                        objectFit: 'cover',
                                        objectPosition: 'center',
                                    }}
                                />
                            </Box>
                        )}

                        {/* Title and Description */}
                        <Stack gap={1}>
                            <Typography
                                variant="h3"
                                color={customizations.productCardTextColor}
                                fontSize={theme.typography.fontSize * 1.5}
                                lineHeight={1.1}
                                fontWeight="bold"
                            >
                                {title}
                            </Typography>
                            {description && (
                                <Typography
                                    variant="body2"
                                    color={customizations.productCardTextColor}
                                    sx={{ opacity: 0.8 }}
                                >
                                    {description}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>

                    {/* RIGHT COLUMN (landscape) / BOTTOM SECTION (portrait): Quantity, Price, Button */}
                    <Stack
                        gap={2}
                        justifyContent="space-between"
                        sx={{
                            '@container (min-width: 600px)': {
                                flex: 1,
                                minWidth: 0,
                                maxWidth: 350,
                            },
                        }}
                    >
                        {/* Quantity inputs - using shared MultipleNumberInput component */}
                        <MultipleNumberInput
                            items={quantityInputs}
                            onChange={onQuantityChange}
                            globalError={error}
                            labelText=""
                            backgroundColor={customizations.productCardColor}
                            color={customizations.productCardTextColor}
                            labelColor={customizations.productCardTextColor}
                            border={`1px solid ${alpha(customizations.productCardTextColor, 0.2)}`}
                        />

                        {/* Price and Button */}
                        <Stack gap={1}>
                            {price && (
                                <Typography
                                    fontWeight={500}
                                    color={customizations.productCardTextColor}
                                    fontSize={theme.typography.fontSize * 1.5}
                                >
                                    {price}
                                </Typography>
                            )}

                            <LoadingButton
                                variant={customizations.productCardPrimaryButtonStyle ?? 'outlined'}
                                onClick={action.onClick}
                                disabled={action.disabled}
                                loading={action.loading}
                                sx={(theme) => ({
                                    width: '100%',
                                    px: 3,
                                    py: 1.5,
                                    fontSize: 16,
                                    borderWidth: 1,
                                    flexShrink: 0,
                                    borderRadius: theme.shape,
                                    color: customizations.productCardPrimaryColor,
                                    backgroundColor: customizations.productCardPrimaryColorContrast,
                                    borderColor: customizations.productCardPrimaryColor,
                                    '&:disabled': {
                                        color: customizations.productCardPrimaryColor,
                                        backgroundColor:
                                            customizations.productCardPrimaryColorContrast,
                                        borderColor: customizations.productCardPrimaryColor,
                                        filter: 'grayscale(0.9)',
                                        opacity: 0.75,
                                    },
                                    '&:hover': {
                                        color: darken(customizations.productCardPrimaryColor, 0.1),
                                        backgroundColor: darken(
                                            customizations.productCardPrimaryColorContrast,
                                            0.1,
                                        ),
                                        borderColor: darken(
                                            customizations.productCardPrimaryColor,
                                            0.1,
                                        ),
                                    },
                                    '&:focus': {
                                        boxShadow: `0 0 0 2px ${customizations.productCardPrimaryColorContrast}, \
                                                    0 0 0 3px ${customizations.productCardPrimaryColor}`,
                                    },
                                })}
                            >
                                {action.label}
                            </LoadingButton>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    );
}
